import React, { useEffect, useMemo } from 'react';
import { Button, FormControl } from 'react-bootstrap';

import { VAT_EXEMPTION_REASONS } from '../../util/enums';
import ValidatorFormGroup from '../main/validator-form-group';
import {
    LoadCountriesDetails,
} from '../../store/countriesDetailsSlice';
import useTranslate from '../useTranslate';
import {
    ConfirmVatinByPartner,
    ContractDetails,
    isValidVatIdVerificationStatus,
    LoadContractDetails,
    VatIdVerificationErrorType,
    VatIdVerificationStatus,
} from '../../store/userDetailsSlice';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/pro-light-svg-icons/faExclamationTriangle';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import { faSync } from '@fortawesome/pro-light-svg-icons/faSync';
import ConfirmButton from '../util/confirm-button';
import { Link } from 'react-router';
import { usePbDispatch, usePbSelector } from '../../store/pbStoreHooks';
import { useFormikContext } from 'formik';

export type VatExemptionReason =
    | 'VAT_EXEMPT'
    | 'REVERSE_CHARGE'
    | 'SUBJECT_TO_VAT';

interface Props {
    disabled?: boolean;
    countryCode: string;
    vatExempt: boolean;
    vatId: string | null | undefined;
    taxNumber: string | null | undefined;
    vatIdVerificationStatus?: VatIdVerificationStatus;
    vatIdVerificationErrorType?: VatIdVerificationErrorType;
    vatIdVerificationError?: string;
    hideVatIdError: boolean;
    hideTaxNumberError: boolean;
    vatIdDirty?: boolean;
    reason: VatExemptionReason;

    onChangeVatSetting: (value: VatExemptionReason) => void;
    onChangeVatId: (vatId: string | null, taxNumber: string | null) => void;
    onChangeVatIdType: (vatId: string | null, taxNumber: string | null) => void;
}

enum NumberType {
    NONE,
    VATIN,
    TAX_NUMBER,
}

function FieldVatIdAndExemptionReason(props: Readonly<Props>) {
    const strings = useTranslate('FieldVatExemptionReason');
    const dispatch = usePbDispatch();
    const countriesDetails = usePbSelector(state => state.countriesDetails.countriesDetails);

    const formikProps = useFormikContext<ContractDetails>();

    useEffect(() => {
        dispatch(LoadCountriesDetails()).then();
    }, []);

    const optionsForCountry = useMemo(() => {
        if (props.disabled) {
            return [props.reason];
        }
        if (!props.countryCode || !countriesDetails?.countries_details) {
            return [];
        }
        const countryCode = String(props.countryCode).toUpperCase();
        if (
            countriesDetails.countries_details[countryCode]
        ) {
            return countriesDetails.countries_details[countryCode].country_to_vat_exemption_reasons;
        }
        return [];
    }, [
        props.disabled,
        props.reason,
        props.countryCode,
        countriesDetails?.countries_details,
    ]);

    const implicitValue = useMemo(() => {
        if (!props.vatExempt) {
            return VAT_EXEMPTION_REASONS.SUBJECT_TO_VAT;
        }
        if (!optionsForCountry || optionsForCountry.length === 0) {
            return props.reason;
        }
        if (props.reason && optionsForCountry.includes(props.reason)) {
            return props.reason;
        }
        return optionsForCountry[0];
    }, [optionsForCountry, props.reason, props.vatExempt]);

    const selectedReason = useMemo(() => {
        return props.vatExempt
            ? props.reason
            : VAT_EXEMPTION_REASONS.SUBJECT_TO_VAT;
    }, [props.vatExempt, props.reason]);

    function getTranslatedTypes() {
        return strings.byCountry[props.countryCode] ?? strings.default;
    }

    useEffect(() => {
        if (!props.disabled) {
            const newVatSetting = implicitValue;
            if (selectedReason !== newVatSetting) {
                props.onChangeVatSetting(newVatSetting);
            }
        }
    }, [
        props.disabled,
        implicitValue,
        selectedReason,
        props.onChangeVatSetting,
    ]);

    function handleChangeReason(event) {
        const value = event.target.value;
        props.onChangeVatSetting(value);
    }

    function handleBlurVatId(event) {
        return props.onChangeVatId(event.target.value.replace(/\s/g, ''), null);
    }

    function handleBlurTaxNumber(event) {
        return props.onChangeVatId(null, event.target.value.replace(/\s/g, ''));
    }

    function renderOption(type, onChange) {
        const translatedTypes = getTranslatedTypes();

        return (
            <div style={{ display: 'flex' }} key={String(type)}>
                <input
                    style={{ alignSelf: 'start' }}
                    disabled={props.disabled}
                    type="radio"
                    id={type}
                    name={type}
                    key={type}
                    value={type}
                    onChange={onChange}
                    checked={type === selectedReason}
                />
                <label style={{ marginLeft: '0.5em' }} htmlFor={type}>
                    {translatedTypes[type] || type}
                </label>
            </div>
        );
    }

    function handleConfirm(event?: React.MouseEvent<HTMLAnchorElement>) {
        event?.preventDefault();
        dispatch(ConfirmVatinByPartner(props.vatId ?? '')).then();
    }

    function renderTaxNumberVerificationStatus() {
        return renderVatIdVerificationStatus(true);
    }

    function renderVatIdVerificationStatus(forTaxNumber?: boolean) {
        if (props.vatIdDirty) {
            return (
                <div className={'input-note-description'}>
                    {strings.hintVerificationUnknown}
                </div>
            );
        }

        if (
            !isValidVatIdVerificationStatus(props.vatIdVerificationStatus) &&
            props.vatIdVerificationErrorType === 'COMPANY_WITHOUT_VAT'
        ) {
            return (
                <>
                    <div className={'input-note-warning'}>
                        <FontAwesomeIcon
                            icon={faExclamationTriangle}
                            size={'lg'}
                            color={'#FF4343'}
                        />{' '}
                        {strings.hintVerificationCompanyWithoutVat}
                    </div>
                    <div>
                        <Link
                            to={'/'}
                            className={'btn btn-link btn-action'}
                            onClick={handleConfirm}
                        >
                            {strings.actionVerificationConfirmCompany}
                        </Link>
                    </div>
                </>
            );
        }
        if (forTaxNumber) {
            return null;
        }

        switch (props.vatIdVerificationStatus) {
            case 'INVALID':
                return (
                    <>
                        <div className={'input-note-warning'}>
                            <FontAwesomeIcon
                                icon={faExclamationTriangle}
                                size={'lg'}
                                color={'#FF4343'}
                            />{' '}
                            {getVatinError()}
                        </div>
                        {maybeRenderOverride()}
                    </>
                );
            case 'CHECK_REQUIRED':
                return (
                    <>
                        <div className={'input-note-description'}>
                            {strings.hintVerificationPending}
                        </div>
                        {props.disabled && (
                            <div>
                                <Button
                                    bsStyle={'link'}
                                    onClick={handleRefresh}
                                >
                                    <FontAwesomeIcon
                                        icon={faSync}
                                        size={'sm'}
                                    />{' '}
                                    {strings.actionRefresh}
                                </Button>
                            </div>
                        )}
                    </>
                );
            case 'TECHNICAL_ERROR':
                return (
                    <>
                        <div className={'input-note-description'}>
                            {strings.hintVerificationTechnicalError}
                        </div>
                        {maybeRenderOverride()}
                    </>
                );
            case 'CONFIRMED_BY_PARTNER':
                return (
                    <div className={'input-note-description'}>
                        <FontAwesomeIcon icon={faCheck} size={'sm'} />{' '}
                        {strings.hintVerificationConfirmed}
                    </div>
                );
            case 'DO_NOT_CHECK':
                return (
                    <div className={'input-note-description'}>
                        <FontAwesomeIcon icon={faCheck} size={'sm'} />{' '}
                        {strings.hintVerificationNotChecked}
                    </div>
                );
            case 'VALID':
                return (
                    <div className={'input-note-description'}>
                        <FontAwesomeIcon icon={faCheck} size={'sm'} />{' '}
                        {strings.hintVerificationSuccess}
                    </div>
                );
        }
    }

    function getVatinError() {
        if (props.vatIdVerificationErrorType === 'MISMATCHED_COMPANY_NAME') {
            return strings._utils.sprintf(
                strings.hintVerificationMismatchedCompanyName,
                props.vatIdVerificationError
            );
        } else if (props.vatIdVerificationErrorType === 'COMPANY_WITHOUT_VAT') {
            return strings.hintVerificationCompanyWithoutVat;
        }
        return strings.hintVerificationFailure;
    }

    function maybeRenderOverride() {
        return (
            <div>
                <ConfirmButton
                    onConfirmedAction={handleConfirm}
                    confirmTitle={strings.modalConfirmTitle}
                    confirmMessage={strings.modalConfirmText}
                    confirmAction={strings.modalConfirmConfirm}
                    rejectAction={strings.modalConfirmReject}
                    linkClassName={'btn btn-link btn-action'}
                >
                    {strings.textVerificationConfirm}{' '}
                    {strings.actionVerificationConfirm}
                </ConfirmButton>
            </div>
        );
    }

    function renderVatId() {
        const vatLiable =
            selectedReason === VAT_EXEMPTION_REASONS.SUBJECT_TO_VAT;
        const numberType = getNumberType(props);
        const isDisabled = props.disabled;
        const labelRequired = vatLiable ? `*` : '';
        return (
            <div style={{ marginLeft: '1em', marginTop: '1em' }}>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: '1em',
                    }}
                >
                    <div>
                        <input
                            type="radio"
                            id="radio-vat-id"
                            disabled={isDisabled}
                            checked={numberType === NumberType.VATIN}
                            onChange={() =>
                                props.onChangeVatIdType(
                                    props.vatId ?? props.taxNumber ?? '',
                                    null
                                )
                            }
                        ></input>
                        <label
                            style={{ marginLeft: '0.5em' }}
                            htmlFor="radio-vat-id"
                        >
                            {strings.vat_id}
                        </label>
                    </div>
                    <div>
                        <input
                            type="radio"
                            id="radio-tax-number"
                            disabled={isDisabled}
                            checked={numberType === NumberType.TAX_NUMBER}
                            onChange={() =>
                                props.onChangeVatIdType(
                                    null,
                                    props.taxNumber ?? props.vatId ?? ''
                                )
                            }
                        ></input>
                        <label
                            style={{ marginLeft: '0.5em' }}
                            htmlFor="radio-tax-number"
                        >
                            {strings.tax_number}
                        </label>
                    </div>
                    <div hidden={!props.vatExempt}>
                        <input
                            type="radio"
                            id="radio-no-number"
                            disabled={isDisabled}
                            checked={numberType === NumberType.NONE}
                            onChange={() => props.onChangeVatIdType(null, null)}
                        ></input>
                        <label
                            style={{ marginLeft: '0.5em' }}
                            htmlFor="radio-no-number"
                        >
                            {strings.no_number}
                        </label>
                    </div>
                    <div></div>
                </div>
                <div hidden={numberType !== NumberType.VATIN}>
                    <ValidatorFormGroup
                        label={`${strings.vat_id}${labelRequired}`}
                        hideErrorLabel={props.hideVatIdError}
                        errorLabel={strings.vatError}
                    >
                        <FormControl
                            type="text"
                            className={'input-text-color'}
                            value={props.vatId ?? ''}
                            name="vat_id"
                            onChange={formikProps.handleChange}
                            onBlur={handleBlurVatId}
                            disabled={isDisabled}
                            pattern={'^([A-Z]{2})(.{2,30})$'}
                        />
                    </ValidatorFormGroup>
                    {renderVatIdVerificationStatus()}
                </div>
                <div hidden={numberType !== NumberType.TAX_NUMBER}>
                    <ValidatorFormGroup
                        label={`${strings.tax_number}${labelRequired}`}
                        hideErrorLabel={props.hideTaxNumberError}
                        errorLabel={strings.vatError}
                    >
                        <FormControl
                            type="text"
                            className={'input-text-color'}
                            value={props.taxNumber ?? ''}
                            name="tax_number"
                            onChange={formikProps.handleChange}
                            onBlur={handleBlurTaxNumber}
                            disabled={isDisabled}
                        />
                    </ValidatorFormGroup>
                    {renderTaxNumberVerificationStatus()}
                </div>
                <div hidden={numberType !== NumberType.NONE}>
                    {renderVatIdVerificationStatus()}
                </div>
            </div>
        );
    }

    function renderOptions() {
        if (props.disabled) {
            return (
                <div>{renderOption(selectedReason, handleChangeReason)}</div>
            );
        } else {
            const options = [
                VAT_EXEMPTION_REASONS.SUBJECT_TO_VAT,
                ...optionsForCountry,
            ];
            return (
                <div>
                    {options.map((it) => renderOption(it, handleChangeReason))}
                </div>
            );
        }
    }

    function handleRefresh(event: React.MouseEvent<Button>) {
        event.preventDefault();
        dispatch(LoadContractDetails()).then();
    }

    return (
        <ValidatorFormGroup label="" hideErrorLabel>
            {renderOptions()}
            {renderVatId()}
        </ValidatorFormGroup>
    );
}

function getNumberType(
    props: Readonly<{
        vatExempt: boolean;
        vatId: string | null | undefined;
        taxNumber: string | null | undefined;
    }>
) {
    if (props.vatExempt && props.vatId == null && props.taxNumber == null) {
        return NumberType.NONE;
    }
    if (props.taxNumber != null) {
        return NumberType.TAX_NUMBER;
    } else {
        return NumberType.VATIN;
    }
}

export default FieldVatIdAndExemptionReason;
